import is from '@sindresorhus/is';
import clsx from 'clsx';
import { type FunctionComponent, memo, useEffect, useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { CardWithContextMenu } from '~/ui/core/card/CardWithContextMenu';
import { IconAsterisk, IconCheckmark1Small, IconChevronDownSmall, IconChevronTopSmall } from '~/ui/core/icons';
import { SpinningIcon } from '~/ui/domain/jsa-progress/JsaProgress';

import {
    isSetupTaskId,
    isSetupTaskOpenable,
    type SetupTaskCompletionState,
    STEPS,
    type StepId,
    stepIdToSubtitleKey,
    stepIdToTitleKey,
    type TaskId,
    taskIdToCtaKey,
    taskIdToDescriptionKey,
    taskIdToTitleKey,
} from '../constants';
import { getTaskPanelComponent } from '../task-panel-registry';

type StepStatus = 'active' | 'completed' | 'upcoming';

type TaskRowProps = {
    readonly isComplete: boolean;
    readonly isFirstActionable: boolean;
    readonly isOpen: boolean;
    readonly isOpenable: boolean;
    readonly onSelect: (taskId: TaskId) => void;
    readonly showGenerating: boolean;
    readonly taskId: TaskId;
};

type TaskRowSuffixProps = {
    readonly ctaLabel: string;
    readonly isComplete: boolean;
    readonly isFirstActionable: boolean;
    readonly isOpen: boolean;
    readonly onCtaClick: () => void;
    readonly showCta: boolean;
    readonly showGenerating: boolean;
};

const TaskRowSuffix: FunctionComponent<TaskRowSuffixProps> = ({
    ctaLabel,
    isComplete,
    isFirstActionable,
    isOpen,
    onCtaClick,
    showCta,
    showGenerating,
}) => {
    const translate = useTranslate();

    if (showGenerating) {
        return (
            <div className="flex flex-row items-center gap-m text-gray-800">
                <SpinningIcon size="1.25rem" spinning={true}>
                    <IconAsterisk />
                </SpinningIcon>
                <span className="text-sm font-medium leading-[1.25rem]">
                    {translate('pages.job-architecture-v2.setup-dialog.generating.title')}
                </span>
            </div>
        );
    }

    if (isComplete) {
        return <Badge variant="success">{translate('pages.installation-guide.tasks.done')}</Badge>;
    }

    if (isOpen) {
        return <Badge variant="gray">{translate('pages.installation-guide.tasks.in-progress')}</Badge>;
    }

    if (isFirstActionable && showCta) {
        return (
            <Button
                variant="primary"
                onClick={(event) => {
                    event.stopPropagation();
                    onCtaClick();
                }}
            >
                {ctaLabel}
            </Button>
        );
    }

    return null;
};

const TaskRow: FunctionComponent<TaskRowProps> = memo(
    ({ isComplete, isFirstActionable, isOpen, isOpenable, onSelect, showGenerating, taskId }) => {
        const translate = useTranslate();
        const hasPanel = !is.undefined(getTaskPanelComponent(taskId));
        const isFutureTask = !isComplete && !isOpen && !isFirstActionable;
        const isInteractive = hasPanel && isOpenable && !isFutureTask && !showGenerating;
        const isHighlighted = isOpen || (isFirstActionable && (hasPanel || showGenerating));

        const handleSelect = (): void => {
            if (isInteractive) {
                onSelect(taskId);
            }
        };

        return (
            <div
                className={clsx(
                    'transition-opacity duration-500 ease',
                    isComplete && !showGenerating && 'opacity-60 hover:opacity-100',
                    (isFutureTask || !isInteractive) && 'pointer-events-none',
                )}
                data-active={isHighlighted}
                data-testid={`installation-guide-task-${taskId}`}
            >
                <CardWithContextMenu
                    id={taskId}
                    isSelected={isHighlighted}
                    subtitle={
                        <span className="!text-sm text-gray-800">{translate(taskIdToDescriptionKey[taskId])}</span>
                    }
                    suffix={
                        <TaskRowSuffix
                            ctaLabel={translate(taskIdToCtaKey[taskId])}
                            isComplete={isComplete}
                            isFirstActionable={isFirstActionable}
                            isOpen={isOpen}
                            showCta={hasPanel}
                            showGenerating={showGenerating}
                            onCtaClick={handleSelect}
                        />
                    }
                    suffixAlignment="center"
                    title={translate(taskIdToTitleKey[taskId])}
                    onSelect={handleSelect}
                />
            </div>
        );
    },
);

type StepHeaderBadgeProps = {
    readonly doneCount: number;
    readonly isLoadingThisStep: boolean;
    readonly status: StepStatus;
    readonly taskCount: number;
};

const StepHeaderBadge: FunctionComponent<StepHeaderBadgeProps> = ({
    doneCount,
    isLoadingThisStep,
    status,
    taskCount,
}) => {
    const translate = useTranslate();

    if (status === 'completed') {
        return <Badge variant="success">{translate('pages.installation-guide.steps.completed')}</Badge>;
    }

    if (status !== 'active') {
        return null;
    }

    if (isLoadingThisStep) {
        return (
            <Badge variant="orange">
                <span className="size-[6px] animate-pulse rounded-full bg-orange-500" />
                {translate('pages.installation-guide.tasks.in-progress')}
            </Badge>
        );
    }

    if (doneCount > 0) {
        return (
            <span className="text-sm font-medium tracking-tight text-black-600">
                {translate('pages.installation-guide.steps.done-count', { done: doneCount, total: taskCount })}
            </span>
        );
    }

    return <Badge variant="gray">{translate('pages.installation-guide.steps.ready-to-start')}</Badge>;
};

type StepCircleProps = {
    readonly status: StepStatus;
    readonly stepNumber: number;
};

const StepCircle: FunctionComponent<StepCircleProps> = ({ status, stepNumber }) => {
    if (status === 'completed') {
        return (
            <div className="flex size-[2rem] items-center justify-center rounded-full bg-success-500 [&_svg]:size-4 [&_svg]:text-white-200">
                <IconCheckmark1Small />
            </div>
        );
    }

    if (status === 'active') {
        return (
            <div className="flex size-[2rem] items-center justify-center rounded-full bg-black-900 text-sm font-semibold tracking-tight text-white-200">
                {stepNumber}
            </div>
        );
    }

    return (
        <div className="flex size-[2rem] items-center justify-center rounded-full border-2 border-gray-300 bg-white-200 text-sm font-medium tracking-tight text-gray-400">
            {stepNumber}
        </div>
    );
};

type TimelineStepProps = {
    readonly completion: SetupTaskCompletionState;
    readonly isCompletionLoading: boolean;
    readonly isExpanded: boolean;
    readonly isGenerating: boolean;
    readonly isLast: boolean;
    readonly onTaskSelect: (taskId: TaskId) => void;
    readonly onToggle: () => void;
    readonly openTaskId: TaskId | null;
    readonly status: StepStatus;
    readonly stepId: StepId;
    readonly stepNumber: number;
    readonly tasks: readonly TaskId[];
};

const isTaskComplete = (taskId: TaskId, completion: SetupTaskCompletionState): boolean =>
    isSetupTaskId(taskId) && completion[taskId];

type StepHeaderProps = {
    readonly doneCount: number;
    readonly isExpanded: boolean;
    readonly isLoadingThisStep: boolean;
    readonly onToggle: () => void;
    readonly status: StepStatus;
    readonly stepId: StepId;
    readonly taskCount: number;
};

const StepHeader: FunctionComponent<StepHeaderProps> = ({
    doneCount,
    isExpanded,
    isLoadingThisStep,
    onToggle,
    status,
    stepId,
    taskCount,
}) => {
    const translate = useTranslate();
    const isUpcoming = status === 'upcoming';

    return (
        <button
            aria-expanded={isExpanded}
            className={clsx(
                'group flex w-full items-start justify-between gap-m border-0 bg-transparent p-0 text-left font-sans outline-none transition duration-200 ease focus:outline-none',
                isUpcoming ? 'cursor-default' : 'cursor-pointer hover:opacity-80',
            )}
            disabled={isUpcoming}
            type="button"
            onClick={onToggle}
        >
            <div className="flex flex-col">
                <span
                    className={clsx(
                        'text-lg font-medium leading-6 tracking-tight',
                        status === 'active' && 'text-black-900',
                        status === 'completed' && 'text-black-800',
                        isUpcoming && 'text-gray-400',
                    )}
                >
                    {translate(stepIdToTitleKey[stepId])}
                </span>
                <span
                    className={clsx(
                        'text-lg font-medium leading-6 tracking-tight',
                        isUpcoming ? 'text-gray-400' : 'text-gray-700',
                    )}
                >
                    {translate(stepIdToSubtitleKey[stepId])}
                </span>
            </div>
            <div className="flex shrink-0 flex-row items-center gap-m">
                <StepHeaderBadge
                    doneCount={doneCount}
                    isLoadingThisStep={isLoadingThisStep}
                    status={status}
                    taskCount={taskCount}
                />
                {!isUpcoming ? (
                    <span
                        aria-hidden="true"
                        className="flex items-center justify-center text-gray-500 transition duration-200 ease group-hover:text-black-800 [&_svg]:size-3.5"
                    >
                        {isExpanded ? <IconChevronTopSmall /> : <IconChevronDownSmall />}
                    </span>
                ) : null}
            </div>
        </button>
    );
};

const TimelineStep: FunctionComponent<TimelineStepProps> = ({
    completion,
    isCompletionLoading,
    isExpanded,
    isGenerating,
    isLast,
    onTaskSelect,
    onToggle,
    openTaskId,
    status,
    stepId,
    stepNumber,
    tasks,
}) => {
    const translate = useTranslate();
    const isCompleted = status === 'completed';
    const isUpcoming = status === 'upcoming';
    const doneCount = tasks.filter((taskId) => isTaskComplete(taskId, completion)).length;
    const firstNotDoneTaskId = tasks.find((taskId) => !isTaskComplete(taskId, completion));
    const isLoadingThisStep = isGenerating && tasks.includes('build-foundation');
    const showExpandedBody = isExpanded && !isUpcoming;

    return (
        <li
            className={clsx(
                'relative flex flex-row gap-xl transition-opacity duration-500 ease',
                isCompleted && 'opacity-55',
                isUpcoming && 'opacity-60',
            )}
        >
            {!isLast ? (
                <span
                    aria-hidden="true"
                    className={clsx(
                        'absolute bottom-0 left-[15px] top-[36px] w-[2px] transition-colors duration-500 ease',
                        isCompleted ? 'bg-success-300' : 'bg-gray-200',
                    )}
                />
            ) : null}
            <div className="relative z-10 shrink-0">
                <StepCircle status={status} stepNumber={stepNumber} />
            </div>
            <div className="flex-1 pb-3xl">
                <StepHeader
                    doneCount={doneCount}
                    isExpanded={isExpanded}
                    isLoadingThisStep={isLoadingThisStep}
                    status={status}
                    stepId={stepId}
                    taskCount={tasks.length}
                    onToggle={onToggle}
                />
                {!isExpanded && isCompleted ? (
                    <span className="mt-xxs block text-xs text-gray-500">
                        {translate('pages.installation-guide.steps.tasks-completed', { count: tasks.length })}
                    </span>
                ) : null}
                {showExpandedBody ? (
                    <div className="mt-l flex flex-col gap-s rounded-xxxl bg-gray-200 p-l">
                        {tasks.map((taskId) => (
                            <TaskRow
                                key={taskId}
                                isComplete={isTaskComplete(taskId, completion)}
                                isFirstActionable={
                                    status === 'active' && taskId === firstNotDoneTaskId && openTaskId !== taskId
                                }
                                isOpen={openTaskId === taskId}
                                isOpenable={
                                    isSetupTaskId(taskId) &&
                                    isSetupTaskOpenable(taskId, completion, isCompletionLoading)
                                }
                                showGenerating={taskId === 'build-foundation' && isGenerating}
                                taskId={taskId}
                                onSelect={onTaskSelect}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
        </li>
    );
};

const isStepComplete = (tasks: readonly TaskId[], completion: SetupTaskCompletionState): boolean =>
    tasks.every((taskId) => isTaskComplete(taskId, completion));

type TimelineProps = {
    readonly completion: SetupTaskCompletionState;
    readonly isCompletionLoading: boolean;
    readonly isGenerating: boolean;
    readonly onTaskSelect: (taskId: TaskId) => void;
    readonly openTaskId: TaskId | null;
};

export const Timeline: FunctionComponent<TimelineProps> = memo(
    ({ completion, isCompletionLoading, isGenerating, onTaskSelect, openTaskId }) => {
        const activeStepIndex = STEPS.findIndex((step) => !isStepComplete(step.tasks, completion));
        const resolvedActiveIndex = activeStepIndex === -1 ? STEPS.length - 1 : activeStepIndex;
        const activeStepId = STEPS[resolvedActiveIndex]?.id;
        const [expandedSteps, setExpandedSteps] = useState<ReadonlySet<StepId>>(
            () => new Set(activeStepId === undefined ? [] : [activeStepId]),
        );

        const previousActiveStepIdRef = useRef(activeStepId);

        useEffect(() => {
            if (isCompletionLoading || activeStepId === undefined || previousActiveStepIdRef.current === activeStepId) {
                return;
            }

            previousActiveStepIdRef.current = activeStepId;
            setExpandedSteps(new Set([activeStepId]));
        }, [activeStepId, isCompletionLoading]);

        const toggleStep = (stepId: StepId): void => {
            setExpandedSteps((previous) => {
                const next = new Set(previous);
                if (next.has(stepId)) {
                    next.delete(stepId);
                } else {
                    next.add(stepId);
                }

                return next;
            });
        };

        return (
            <ol className="m-0 flex list-none flex-col p-0">
                {STEPS.map((step, index) => (
                    <TimelineStep
                        key={step.id}
                        completion={completion}
                        isCompletionLoading={isCompletionLoading}
                        isExpanded={expandedSteps.has(step.id)}
                        isGenerating={isGenerating}
                        isLast={index === STEPS.length - 1}
                        openTaskId={openTaskId}
                        status={
                            index < resolvedActiveIndex
                                ? 'completed'
                                : index === resolvedActiveIndex
                                  ? 'active'
                                  : 'upcoming'
                        }
                        stepId={step.id}
                        stepNumber={index + 1}
                        tasks={step.tasks}
                        onTaskSelect={onTaskSelect}
                        onToggle={() => toggleStep(step.id)}
                    />
                ))}
            </ol>
        );
    },
);
